import * as React from 'react';
import { FaIcon } from '@/components/FaIcon';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import classNames from 'clsx';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = ({ ...props }: DialogPrimitive.DialogPortalProps) => (
  <DialogPrimitive.Portal {...props} />
);
DialogPortal.displayName = DialogPrimitive.Portal.displayName;

/**
 * Devolver el foco a quien abrió el diálogo. Radix solo lo hace si el diálogo se abrió
 * con su `<DialogTrigger>`: al cerrar, enfoca ESE trigger y nada más. Casi todos los
 * diálogos de Flash se abren con un `open` controlado desde un botón cualquiera, así que
 * el trigger es `null` y el foco caía en `body`: con teclado o lector de pantalla, al
 * cerrar se volvía al principio de la página.
 *
 * Se guarda el elemento enfocado justo antes de que el foco entre al diálogo
 * (`onOpenAutoFocus` se dispara antes de mover el foco) y se le devuelve al cerrar. Si
 * quien usa el diálogo maneja `onCloseAutoFocus` y hace `preventDefault`, manda lo suyo;
 * si el elemento ya no está en la página (se navegó, se cerró el menú que lo tenía),
 * queda el comportamiento de Radix.
 */
/**
 * Lo que tenía el foco al abrir. Si era un ítem de menú (Radix: "Acciones",
 * "Gestionar"…), ese ítem desaparece al cerrarse el menú y el foco terminaba en
 * `body`: se vuelve al botón que abrió el menú.
 */
const focusOpener = (): HTMLElement | null => {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || active === document.body) return null;
  const menu = active.closest<HTMLElement>('[role="menu"]');
  if (menu) {
    // El contenido del menú apunta a su disparador con `aria-labelledby`; el
    // `aria-controls` del disparador desaparece apenas el menú empieza a cerrarse.
    const labelledBy = menu.getAttribute('aria-labelledby');
    const trigger =
      (labelledBy && document.getElementById(labelledBy)) ||
      (menu.id && document.querySelector<HTMLElement>(`[aria-controls="${CSS.escape(menu.id)}"]`));
    if (trigger instanceof HTMLElement) return trigger;
  }
  return active;
};

const ContentWithReturnFocus = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ onOpenAutoFocus, onCloseAutoFocus, ...props }, ref) => {
  const returnTo = React.useRef<HTMLElement | null>(null);
  return (
    <DialogPrimitive.Content
      ref={ref}
      {...props}
      onOpenAutoFocus={(event) => {
        returnTo.current = focusOpener();
        onOpenAutoFocus?.(event);
      }}
      onCloseAutoFocus={(event) => {
        onCloseAutoFocus?.(event);
        const target = returnTo.current;
        returnTo.current = null;
        if (event.defaultPrevented || !target?.isConnected) return;
        event.preventDefault();
        target.focus({ preventScroll: true });
      }}
    />
  );
});
ContentWithReturnFocus.displayName = DialogPrimitive.Content.displayName;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={classNames(
      'fixed inset-0 z-50 bg-frost-overlay data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/**
 * Un modal que no declara cómo maneja su desborde no scrolleaba: el tope de alto
 * recortaba el contenido y todo lo que quedaba abajo — el resumen y los botones del
 * pie — se dibujaba FUERA de la tarjeta, sin forma de llegar. Se veía con el zoom del
 * navegador al 175% (el viewport queda en ~600px de alto) y en cualquier notebook bajo,
 * y era el bug de "Ajustar precio" cortada. Además el tope de alto solo existía desde
 * `md`, así que entre 640 y 767px la caja crecía sin límite y, al estar centrada por
 * translate, se comía el contenido por arriba Y por abajo.
 *
 * Por eso el default es: acotar el alto en TODOS los breakpoints y dejar que la tarjeta
 * scrollee. Es todo o nada: si el modal declara CUALQUIER cosa de tamaño propio
 * (`overflow-*`, `max-h-*` o un `h-*`) se lo deja tal cual y no se le mezcla nada. Los
 * que se arman en columna con su cuerpo scrolleable quedarían con dos scrolls anidados,
 * y a una hoja a pantalla completa (`!h-[100dvh]`) un tope nuestro le comería el alto.
 */
const declaraSuPropioTamano = (className?: string) =>
  /(?:^|[\s:!])(?:overflow-|max-h-|h-)/.test(className ?? '');

/**
 * Foco al abrir un modal centrado. Radix enfoca el primer control tabulable, que en
 * casi todos los modales es la X de la cabecera: se veía enmarcada en azul al abrir y
 * un lector de pantalla arrancaba por «Cerrar». Ahora:
 * - si hay un campo marcado con `data-autofocus` (o `data-cash-modal-autofocus`), va ahí;
 * - en computadora, al primer campo de texto del formulario;
 * - si no, al contenedor del modal (Tab entra al primer control). En el celular no se
 *   enfoca ningún campo solo, para no levantar el teclado sobre la hoja.
 */
const focusDialogStart = (event: Event) => {
  const content = event.currentTarget as HTMLElement | null;
  if (!content) return;
  event.preventDefault();
  const marked = content.querySelector<HTMLElement>('[data-autofocus],[data-cash-modal-autofocus]');
  const isDesktop = typeof window !== 'undefined' && window.matchMedia?.('(min-width: 640px)').matches;
  const field = isDesktop
    ? content.querySelector<HTMLElement>(
        'input:not([type=hidden]):not([disabled]):not([readonly]),textarea:not([disabled]),select:not([disabled])'
      )
    : null;
  (marked ?? field ?? content).focus({ preventScroll: true });
};

/** Anchos del modal centrado (desde `sm`). Un solo juego para todo Flash. */
const DIALOG_SIZE = {
  sm: 'sm:max-w-[400px]',
  md: 'sm:max-w-[480px]',
  lg: 'sm:max-w-[640px]',
  xl: 'sm:max-w-[760px]'
} as const;

type DialogSize = keyof typeof DIALOG_SIZE;
type DialogMobile = 'full' | 'sheet';

/** Piel común de todo modal centrado: superficie, radio 16, una sombra, padding. */
const DIALOG_SURFACE =
  'bg-[var(--f-card-elevated)] p-6 sm:p-7 shadow-frost-pop focus:outline-none sm:rounded-frost-shell';

/** Movimiento: fundido + 16 px desde abajo al entrar, 12 px al salir; el celular en hoja entra desde abajo. */
const DIALOG_MOTION_CENTER =
  'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:slide-in-from-bottom-4 data-[state=closed]:slide-out-to-bottom-3 data-[state=open]:duration-[320ms] data-[state=open]:ease-frost data-[state=closed]:duration-200 motion-reduce:[--tw-enter-translate-y:0]! motion-reduce:[--tw-exit-translate-y:0]!';
const DIALOG_MOTION_SHEET =
  'max-sm:data-[state=open]:slide-in-from-bottom-full max-sm:data-[state=closed]:slide-out-to-bottom-full';

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    overlayClassName?: string;
    /** Ancho desde `sm`: 400 / 480 (default) / 640 / 760. */
    size?: DialogSize;
    /**
     * Forma en el celular: `full` (default) ocupa la pantalla; `sheet` es una hoja
     * inferior con asa, hasta 92dvh, para modales cortos (confirmaciones, pickers,
     * formularios de pocos campos).
     */
    mobile?: DialogMobile;
    /**
     * `column`: cabecera fija · `DialogBody` con scroll · `DialogActionBar` al pie.
     * Sin esto (default) scrollea la tarjeta entera.
     */
    layout?: 'scroll' | 'column';
    /**
     * Dónde portalear. Por defecto `document.body`. Desde adentro de un `<dialog>`
     * nativo abierto con `showModal()` hay que portalear DENTRO de ese `<dialog>`:
     * está en el top layer y todo lo de `body` queda detrás e inerte.
     */
    container?: HTMLElement | null;
  }
>(
  (
    {
      className,
      overlayClassName,
      size = 'md',
      mobile = 'full',
      layout = 'scroll',
      container,
      onOpenAutoFocus,
      children,
      ...props
    },
    ref
  ) => {
    const sheet = mobile === 'sheet';
    const column = layout === 'column';
    return (
      <DialogPortal container={container ?? undefined}>
        <DialogOverlay className={classNames('!z-scrim', overlayClassName)} />
        <ContentWithReturnFocus
          ref={ref}
          data-layout={layout}
          className={classNames(
            'group/dialog fixed z-modal w-full',
            DIALOG_SURFACE,
            DIALOG_SIZE[size],
            // Escritorio: centrado por translate. Celular: pantalla completa o hoja inferior.
            'sm:left-1/2 sm:top-1/2 sm:bottom-auto sm:h-auto sm:-translate-x-1/2 sm:-translate-y-1/2',
            sheet
              ? 'inset-x-0 bottom-0 top-auto max-h-[92dvh] rounded-t-2xl pt-2.5 pb-[max(1.5rem,env(safe-area-inset-bottom))] max-sm:translate-x-0 max-sm:translate-y-0'
              : 'left-1/2 top-1/2 h-full -translate-x-1/2 -translate-y-1/2 pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:pb-7',
            DIALOG_MOTION_CENTER,
            sheet && DIALOG_MOTION_SHEET,
            column
              ? // Sin padding inferior: la barra de acciones es el pie y llega al borde.
                'flex flex-col overflow-hidden max-h-[100dvh] sm:max-h-[calc(100dvh-2.5rem)] !pb-0'
              : !declaraSuPropioTamano(className) &&
                  'max-h-[100dvh] sm:max-h-[calc(100dvh-2.5rem)] overflow-y-auto overscroll-contain',
            className
          )}
          {...props}
          onOpenAutoFocus={(event) => {
            onOpenAutoFocus?.(event);
            if (!event.defaultPrevented) focusDialogStart(event);
          }}
        >
          {sheet && (
            <div
              aria-hidden
              className="mx-auto mb-4 h-1 w-10 shrink-0 rounded-full bg-frost-hair-stronger sm:hidden"
            />
          )}
          {children}
        </ContentWithReturnFocus>
      </DialogPortal>
    );
  }
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

// `overlayClassName` existe para poder bajar el par overlay+contenido por debajo de la
// capa de dialogos (2147483005/6). Una pantalla completa que se apile POR ENCIMA de esa
// capa deja ciego a cualquier confirm() abierto desde adentro: el dialogo se monta, queda
// detras, y el usuario ve un boton que no responde. Sin esto solo se puede bajar el
// contenido, y el panel queda debajo de su propia cortina.
const DialogContentFullScreen = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { overlayClassName?: string }
>(({ className, overlayClassName, children, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Overlay
      className={classNames(
        'fixed inset-0 z-sheet bg-frost-overlay transition-opacity duration-200 data-[state=open]:animate-fadeIn data-[state=closed]:animate-fadeOut',
        overlayClassName
      )}
    />
    <ContentWithReturnFocus
      ref={ref}
      className={classNames(
        'fixed overflow-y-auto inset-0 z-modal bg-[rgba(var(--f-paper-rgb),0.99)] p-6 shadow-frost-md duration-200',
        'data-[state=open]:animate-fadeInAndSlideIn data-[state=closed]:animate-fadeOutAndSlideOut',
        'w-full h-full', // Ocupa todo el ancho y alto de la pantalla
        className
      )}
      {...props}
    >
      {children}
    </ContentWithReturnFocus>
  </DialogPrimitive.Portal>
));

DialogContentFullScreen.displayName = DialogPrimitive.Content.displayName;

const DialogRightContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    autoHeight?: boolean;
    fullScreen?: boolean;
  }
>(({ className, children, autoHeight = false, fullScreen = false, ...props }, ref) => (
  <DialogPortal>
    {/* El detalle lateral deja la pantalla apenas velada. */}
    <DialogOverlay className={fullScreen ? '!z-scrim' : '!bg-[rgba(var(--f-ink-rgb),0.26)]'} />
    <ContentWithReturnFocus
      ref={ref}
      className={classNames(
        fullScreen
          ? 'fixed overflow-y-auto inset-0 z-scrim bg-[rgba(var(--f-paper-rgb),0.99)] p-0 shadow-frost-md duration-200 data-[state=open]:animate-fadeInAndSlideIn data-[state=closed]:animate-fadeOutAndSlideOut w-full h-full'
          : 'fixed top-2 right-2 z-scrim overflow-hidden w-full sm:w-[550px] shadow-frost-pop focus:outline-hidden bg-[var(--f-card-elevated)] data-[state=open]:!duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-right-full sm:rounded-2xl',
        // autoHeight: card grows with its content (capped at viewport). Default: full-height side panel pinned top+bottom.
        !fullScreen && (autoHeight ? 'max-h-[calc(100dvh-16px)]' : 'bottom-2 h-[calc(100dvh-16px)]'),
        // En el celular el panel ocupa la pantalla entera: sin el marco de 8 px de cortina.
        !fullScreen && 'max-sm:inset-0 max-sm:h-[100dvh] max-sm:max-h-none',
        className
      )}
      {...props}
      // Al abrir, el foco va al panel (no al primer botón): la flecha de cerrar no aparece
      // enmarcada en azul cada vez que se abre con el mouse. Tab sigue entrando al panel.
      onOpenAutoFocus={(event) => {
        props.onOpenAutoFocus?.(event);
        if (event.defaultPrevented) return;
        event.preventDefault();
        (event.target as HTMLElement | null)?.focus?.({ preventScroll: true });
      }}
    >
      {children}
    </ContentWithReturnFocus>
  </DialogPortal>
));

// Misma capa que `DialogContent`: la hoja se abre muchas veces desde un modal (la
// confirmación de `ConfirmationModal` en el celular), y en una capa más baja quedaba
// detrás de la pantalla completa del modal: el botón parecía no hacer nada.
const DialogContentHalfBottom = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    /** Igual que en `DialogContent`: destino del portal (por defecto `document.body`). */
    container?: HTMLElement | null;
  }
>(({ className, container, children, ...props }, ref) => (
  <DialogPortal container={container ?? undefined}>
    <DialogOverlay className="!z-scrim" />
    <ContentWithReturnFocus
      ref={ref}
      className={classNames(
        'fixed inset-x-0 bottom-0 z-modal max-h-[92dvh] w-full bg-[var(--f-card-elevated)] px-5 pt-2.5 pb-[max(24px,env(safe-area-inset-bottom))] rounded-t-2xl shadow-frost-pop duration-200 overflow-y-auto data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
        className
      )}
      {...props}
    >
      <div aria-hidden className="mx-auto mb-4 h-1 w-10 rounded-full bg-frost-hair-stronger" />
      {children}
    </ContentWithReturnFocus>
  </DialogPortal>
));
const DialogContentMenuMobile = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <ContentWithReturnFocus
      ref={ref}
      className={classNames(
        'fixed flex flex-col right-0 top-0 w-full overflow-y-scroll h-[100svh] p-4 transition-transform ease-linear z-modal gap-4 bg-[rgba(var(--f-paper-rgb),0.99)] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-right-1/2 data-[state=open]:slide-in-from-right-1/2',
        className
      )}
      {...props}
    >
      {children}
    </ContentWithReturnFocus>
  </DialogPortal>
));

/**
 * Movimiento de los modales con diseño propio (`DialogContentBare`). Mismos tiempos
 * que el modal de confirmación: 320 ms al entrar con `ease-frost`, 200 ms al salir con
 * la curva estándar. Con movimiento reducido queda solo el fundido.
 */
const BARE_TIMING =
  'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:duration-[320ms] data-[state=open]:ease-frost data-[state=closed]:duration-200 data-[state=closed]:ease-[cubic-bezier(0.4,0,0.2,1)]';

const BARE_MOTION = {
  /** Tarjeta centrada: sube 16px al entrar y baja 12px al salir. */
  center:
    'data-[state=open]:slide-in-from-bottom-4 data-[state=closed]:slide-out-to-bottom-3 motion-reduce:[--tw-enter-translate-y:0]! motion-reduce:[--tw-exit-translate-y:0]!',
  /** Panel lateral: entra y sale por la derecha. */
  right:
    'data-[state=open]:slide-in-from-right-full data-[state=closed]:slide-out-to-right-full motion-reduce:[--tw-enter-translate-x:0]! motion-reduce:[--tw-exit-translate-x:0]!',
  /** Hoja inferior: entra y sale por abajo. */
  bottom:
    'data-[state=open]:slide-in-from-bottom-full data-[state=closed]:slide-out-to-bottom-full motion-reduce:[--tw-enter-translate-y:0]! motion-reduce:[--tw-exit-translate-y:0]!'
} as const;

/**
 * Contenido "sin piel": portal + cortina + contenido de Radix — trampa de foco, Escape,
 * clic afuera, bloqueo del scroll de atrás y entrada/salida animadas — SIN fondo,
 * borde, padding, tamaño ni posición propios. Es para los modales que ya tienen su
 * diseño armado (tarjeta de vidrio, panel lateral) y antes lo montaban a mano con un
 * `fixed inset-0`: pasan su forma tal cual por `className`/`style`, y la cortina por
 * `overlayClassName`/`overlayStyle` (color, blur, z-index).
 *
 * Al abrir, el foco va al contenedor (no al primer control): no levanta el teclado en
 * mobile ni pinta un anillo que antes no estaba; Tab entra al primer control.
 */
const DialogContentBare = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    motion?: keyof typeof BARE_MOTION;
    overlayClassName?: string;
    overlayStyle?: React.CSSProperties;
    container?: HTMLElement | null;
    /**
     * Portalear a un ancla en el lugar del árbol donde está el `<Dialog>` en vez de a
     * `body`. Para los paneles que antes se montaban en línea: conservan el stacking
     * context de la página (p. ej. quedan debajo de la barra, como siempre).
     */
    inPlace?: boolean;
  }
>(
  (
    {
      className,
      motion = 'center',
      overlayClassName,
      overlayStyle,
      container,
      inPlace = false,
      onOpenAutoFocus,
      onPointerDownOutside,
      children,
      ...props
    },
    ref
  ) => {
    const innerRef = React.useRef<HTMLDivElement | null>(null);
    const overlayRef = React.useRef<HTMLDivElement | null>(null);
    const [anchor, setAnchor] = React.useState<HTMLDivElement | null>(null);
    const setRefs = React.useCallback(
      (node: HTMLDivElement | null) => {
        innerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref]
    );
    return (
      <>
        {inPlace && <div ref={setAnchor} className="contents" />}
        {inPlace && !anchor ? null : (
          <DialogPortal container={(inPlace ? anchor : container) ?? undefined}>
            <DialogPrimitive.Overlay
              ref={overlayRef}
              className={classNames('fixed inset-0', BARE_TIMING, overlayClassName)}
              style={overlayStyle}
            />
            <ContentWithReturnFocus
              ref={setRefs}
              aria-describedby={undefined}
              // Solo la cortina cierra, como en los overlays hechos a mano: un toque en un
              // toast (que vive fuera del diálogo) no tiene que cerrar el panel.
              onPointerDownOutside={(event) => {
                onPointerDownOutside?.(event);
                if (event.defaultPrevented) return;
                if (event.detail.originalEvent.target !== overlayRef.current) event.preventDefault();
              }}
              onOpenAutoFocus={(event) => {
                onOpenAutoFocus?.(event);
                if (event.defaultPrevented) return;
                event.preventDefault();
                innerRef.current?.focus({ preventScroll: true });
              }}
              className={classNames('focus:outline-none', BARE_TIMING, BARE_MOTION[motion], className)}
              {...props}
            >
              {children}
            </ContentWithReturnFocus>
          </DialogPortal>
        )}
      </>
    );
  }
);
DialogContentBare.displayName = 'DialogContentBare';

/**
 * Título sin estilos propios, para usar con `asChild` sobre el encabezado que el modal
 * ya tiene: le da nombre accesible al diálogo sin sumarle clases (`DialogTitle` sí
 * agrega tamaño y color, que con `asChild` se mezclarían con los del encabezado).
 */
const DialogTitleBare = DialogPrimitive.Title;

/**
 * Cabecera del modal. Con `title` arma la fila canónica: (Atrás) · título 18/19 semibold
 * · descripción 13 · X de 36 px arriba a la derecha, todo alineado a la izquierda. Sin
 * `title` es un contenedor neutro para cabeceras armadas a mano (compatibilidad).
 */
const DialogHeader = ({
  className,
  title,
  description,
  onBack,
  backLabel = 'Atrás',
  hideClose = false,
  children,
  ...props
}: Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> & {
  title?: React.ReactNode;
  description?: React.ReactNode;
  onBack?: () => void;
  backLabel?: string;
  hideClose?: boolean;
}) => {
  if (title === undefined && description === undefined && !onBack) {
    return (
      <div
        className={classNames(
          'flex items-center justify-between w-full sm:items-start text-center',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
  return (
    <div className={classNames('flex shrink-0 items-start justify-between gap-4', className)} {...props}>
      <div className="flex min-w-0 flex-1 flex-col gap-1 pt-0.5 text-left">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="mb-1 inline-flex min-h-6 w-fit items-center gap-1.5 rounded-frost-chip text-fs-12 font-semibold text-frost-ink-mid transition-colors hover:text-frost-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-frost-brand"
          >
            <FaIcon name="chevron_left" className="text-fs-11" />
            {backLabel}
          </button>
        )}
        {title !== undefined && <DialogTitle>{title}</DialogTitle>}
        {description !== undefined && <DialogDescription>{description}</DialogDescription>}
        {children}
      </div>
      {!hideClose && <DialogClose className="-mr-2 -mt-1.5" />}
    </div>
  );
};
DialogHeader.displayName = 'DialogHeader';

/**
 * Cuerpo con scroll propio para `layout="column"`: ocupa lo que queda entre la cabecera
 * y la barra de acciones y scrollea solo él. El sangrado de 4 px evita que los anillos
 * de foco de los campos se recorten contra el borde del scroll.
 */
const DialogBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={classNames('-mx-1 my-5 min-h-0 flex-1 overflow-y-auto overscroll-contain px-1', className)}
    {...props}
  />
);
DialogBody.displayName = 'DialogBody';

const DialogClose = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Close
    ref={ref}
    aria-label="Cerrar"
    className={classNames(
      'inline-flex shrink-0 items-center justify-center w-9 h-9 rounded-frost-button bg-transparent text-frost-ink-mid hover:text-frost-ink hover:bg-frost-ink/[0.04] active:scale-95 transition-[color,background-color,transform] duration-[120ms] ease-frost motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-frost-brand',
      className
    )}
    {...props}
  >
    <FaIcon name="xmark" className="text-fs-13" />
  </DialogPrimitive.Close>
));
DialogClose.displayName = DialogPrimitive.Close.displayName;

const DialogFooter = ({
  className,
  fixed = false,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { fixed?: boolean }) => (
  <div
    className={classNames(
      'flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end',
      fixed && 'absolute bottom-0 left-0 right-0 p-6',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

/**
 * Barra de acciones de un modal. La regla del sistema: en mobile las acciones van
 * SIEMPRE al pie y pegadas al borde inferior — nunca en el header, donde compiten
 * con el título, quedan lejos del pulgar y se pierden al scrollear un formulario
 * largo. Los botones ocupan la mitad del ancho cada uno en mobile y se alinean a la
 * derecha desde `sm`.
 *
 * Se monta como último hijo de un modal armado en columna: header fijo · cuerpo con
 * `overflow-y-auto` · esta barra. `sticky` (no `fixed`) para que también quede al
 * pie en un diálogo centrado de desktop.
 */
const DialogActionBar = ({
  className,
  hint,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { hint?: React.ReactNode }) => (
  <div
    className={classNames(
      'sticky bottom-0 z-10 flex shrink-0 flex-col gap-2.5 border-t border-frost-hair-strong bg-frost-glass-strong px-5 pt-3.5 pb-[max(0.875rem,env(safe-area-inset-bottom))] lg:backdrop-blur-frost sm:flex-row sm:items-center sm:gap-3 sm:px-6 sm:pb-3.5',
      // Dentro de una tarjeta con padding (`layout="column"`) la barra sale al borde.
      'group-data-[layout=column]/dialog:-mx-6 sm:group-data-[layout=column]/dialog:-mx-7',
      hint ? 'sm:justify-between' : 'sm:justify-end',
      className
    )}
    {...props}
  >
    {hint ? <div className="text-fs-11 leading-snug text-frost-ink-soft">{hint}</div> : null}
    {/* Cada botón lleva `flex-1 sm:flex-none`: mitad y mitad en mobile, ancho natural
        en desktop. */}
    <div className="flex items-center gap-2.5 sm:shrink-0">{children}</div>
  </div>
);
DialogActionBar.displayName = 'DialogActionBar';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={classNames(
      'text-fs-18 sm:text-fs-19 font-semibold leading-tight tracking-tight text-frost-ink',
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={classNames('text-fs-13 leading-relaxed text-frost-ink-mid', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export type { DialogSize, DialogMobile };
export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogActionBar,
  DialogTitle,
  DialogDescription,
  DialogClose,
  DialogContentHalfBottom,
  DialogContentMenuMobile,
  DialogRightContent,
  DialogContentFullScreen,
  DialogContentBare,
  DialogTitleBare
};
